import _ from 'lodash';
import {List, Map} from 'immutable';
import _s from 'underscore.string';
import * as tr from 'transliteration';
import {
  test,
  max,
  inc,
  compose,
  extractBool,
  equals,
  safeParseInt,
  identity,
} from '@packages/utilities/fp/utils';
import {maybe, None, Some, Option} from '@packages/utilities/fp/option';
import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import type {WebflowCssProperty} from '@packages/systems/core/utils/WebflowCssSubset';

// This number should be less than Number.MAX_SAFE_INTEGER ie: (9007199254740991) but more than the expected number of files on site.
const MAX_INCREMENT = 1000000;

/**
 * Converts first letter of the string to uppercase.
 * @param  {String} str               String
 */
export function capitalize(str: string): string {
  return _s.capitalize(str);
}

/**
 * Converts first letter of the string to uppercase, and the rest to lowercase, removing dashes.
 * See https://brand-at.webflow.io/tone-of-voice#style-capitalization-casing-typography for more!
 * @param  {String} str               String
 */
export function toSentenceCase(str: string): string {
  if (!str) return str;

  return str
    .replace(/-/g, ' ') // replace dashes with spaces
    .toLowerCase()
    .replace(/^(\w)/, (match) => match.toUpperCase());
}

/**
 * Naturally sort strings like humans would do. None numbers are compared by their ASCII values.
 * @param  {String} str1          String 1
 * @param  {String} str2          String 2
 */
export function naturalCmp(str1: string, str2: string): number {
  return _s.naturalCmp(str1, str2);
}

/**
 * Inserts the substring in the index of the string
 * @param  {String} str           The original array
 * @param  {Number} index         The delimiter
 * @param  {String} substring     The last delimiter
 * @return {String}               String with the substring inserted
 */
export function insert(str: string, index: number, substring: string): string {
  return _s.insert(str, index, substring);
}

/**
 * Join an array into a human readable sentence.
 * @param  {Array} arr                The original array
 * @param  {String} delimiter         The delimiter
 * @param  {String} lastDelimiter     The last delimiter
 * @return {String}                   Sentenced string
 */
export function toSentence(
  arr: Array<string>,
  delimiter?: string,
  lastDelimiter?: string
): string {
  return _s.toSentence(arr, delimiter, lastDelimiter);
}

/**
 * Left trim. Similar to trim, but only for left side.
 * @param  {String} str           The original string.
 * @param  {String} characters    The characters.
 * @return {String}               String trimmed on the left side
 */
export function ltrim(str: string, characters: string): string {
  return _s.ltrim(str, characters);
}

/**
 * Right trim. Similar to trim but only for the right side.
 * @param {string} str
 * @param {string} characters
 * @return {string}
 */
export function rtrim(str: string, characters?: string): string {
  return _s.rtrim(str, characters);
}

/**
 * Returns trimmed one liner.
 * @param {String} string
 * @return {String}
 */
export function trimmedOneLiner(string: string): string {
  return string.trim().replace(/\s*\n+\s*/g, ' ');
}

/**
 * Truncates the string using the length parameter
 * @param  {String} str     The original string.
 * @param  {Number} length  The length
 * @return {String}         String truncated
 * Note: we keep using underscore.string.truncate instead of lodash.trunc because both function have different function definitions with the length argument.
 * E.g: _.trunc("Hello world", 5 => "He...") and _s.truncate("Hello world", 5) => "Hello..."
 */
export function truncate(str: string, length: number): string {
  return _s.truncate(str, length);
}

/**
 * Transform text into a URL slug. Replaces whitespaces, accentuated, and special characters with a dash.
 * @param  {String} str   The original string.
 * @return {String}       String as an URL
 */
export function slugify(str: string): string {
  return str
    ? tr.slugify(str, {
        allowedChars: 'a-zA-Z0-9-',
        replace: [
          ["'", ''],
          ['’', ''],
        ],
      })
    : '';
}

/**
 * Compress some whitespaces to one.
 * @param  {String} str   The original string.
 * @return {String}       String with compressed spaces
 */
export function clean(str: string): string {
  return _s.clean(str);
}

/**
 * Converts underscored or dasherized string to a camelized one.
 * @param  {String} str   The original string.
 * @return {String}       String camelized
 * Note: we keep using underscore.string.camelize instead of lodash.camelCase because both functions have different
        behavior with the uppercase for the first character of the string.
 * E.g: _.camelCase('Foo Bar') => "fooBar" and _s.camelize('Foo Bar') => "FooBar"
 */
export function camelize(str: string): string {
  return _s.camelize(str);
}

/**
 * Removes all html tags from string.
 * @param  {String} str   The original string.
 * @return {String}       String without html tags
 */
export function stripTags(str: string): string {
  return _s.stripTags(str);
}

/**
 * Searches a string from right to left for a pattern and returns a substring
 consisting of the characters in the string that are to the right of the pattern or all string if no match found.
 * @param  {String} str     The original string.
 * @param  {String} pattern The pattern.
 * @return {String}         String resulted of the search
 */
export function strRightBack(str: string, pattern: string): string {
  return _s.strRightBack(str, pattern);
}

/**
 * Converts an underscored, camelized, or dasherized string into a humanized one.
 Also removes beginning and ending whitespace, and removes the postfix '_id'.
 * @param  {String} str   The original string.
 * @return {String}       Humanized String
 */
export function humanize(str: string): string {
  return _s.humanize(str);
}

/**
 * Converts a underscored or camelized string into an dasherized one.
 * @param  {String} str   The original string.
 * @return {String}       Desherized String
 */
export function dasherize(str: string): string {
  return _s.dasherize(str);
}

/**
 * Capitalizes the first letter of each word in the string. This is overriden
 * because newer versions of underscore.string also capitalize letters after a - (dash)
 * i.e. ease-in-out here is Ease-in-out but underscore.string would now output Ease-In-Out
 * @param  {String} str   The original string.
 * @return {String}       String with the first letter of each word capitalized
 */
export function titleize(str: string): string {
  if (str == null) return '';
  return String(str).replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
}

/**
 * Capitalizes the first letter of each word in the string and then removes TODO.
 */
export function classify(str: string): string {
  return titleize(String(str).replace(/[\W_]/g, ' ')).replace(/\s/g, '');
}

/**
 * Searches a string from right to left for a pattern and returns a substring consisting
 of the characters in the string that are to the left of the pattern or all string if no match found.
 * @param  {String} str       The original string.
 * @param  {String} pattern   The pattern.
 * @return {String}           String with the string on the right of the pattern
 */
export function strRight(str: string, pattern: string): string {
  return _s.strRight(str, pattern);
}

/**
 * Searches a string from left to right for a pattern and returns a substring consisting
 of the characters in the string that are to the left of the pattern or all string if no match found.
 * @param  {String} str       The original string.
 * @param  {String} pattern   The pattern.
 * @return {String}           String with the string on the left of the pattern
 */
export function strLeft(str: string, pattern: string): WebflowCssProperty {
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'WebflowCssProperty'.
  return _s.strLeft(str, pattern);
}

/**
 * Checks to see if this string has a value.
 * @param  {String} str   The original string.
 * @return {Boolean}      True if this is a non-empty string.
 */
export function hasValue(str: string): boolean {
  if (!_.isString(str)) {
    return false;
  }

  // \xa0 is &nbsp
  return str.replace(/^[\s\xa0]+|[\s\xa0]+$/g, '').length > 0;
}

/**
 * Collapses multiple consecutive newlines into one.
 * @param  {String} str The original string, e.g. "bob\n\nhas aids"
 * @return {String}     The cleaned string, e.g. "bob\nhas aids"
 */
export function collapseNewLines(str: string): string {
  if (!str || !_.isString(str)) {
    return str;
  }

  return str.replace(/\n\s*\n/g, '\n');
}

/**
 * Removes the last number at the end of a string, including the dash right before the number.
 * @param  {String} str The original string, e.g. "field-2"
 * @return {String}     The clened string, e.g. "field"
 */
export function trimLastNum(str: string): string {
  if (!str || !_.isString(str)) {
    return str;
  }

  return str.replace(/-?[0-9]+$/, '');
}

/**
 * Same as trim, but looks for entire word to remove at the beginning and end of a string.
 * @param  {String} str   The original string. (e.g. "Bob   Bob Hello World Bob ")
 * @param  {String} word  The word to trim. (e.g. "Bob")
 * @return {String}       The clean string. (e.g. "Hello World")
 */
export function trimWord(str: string, word: string): string {
  if (!str || !_.isString(str) || !word) {
    return str;
  }
  str = _.trim(str);

  // Remove trailing + leading words (and spaces)
  while (_s.startsWith(str, word)) {
    str = _.trim(_s.strRight(str, word));
  }
  while (_s.endsWith(str, word)) {
    str = _.trim(_s.strLeftBack(str, word));
  }

  return str;
}

/**
 * Checks to see if a string looks like a URL.
 * @param  {String}   str The string to check.
 * @return {Boolean}      True if the string looks like a URL.
 */
export function isUrl(str: string): boolean {
  if (!str) {
    return false;
  }

  // The presence of unencoded quotation marks means the URL is automatically invalid
  if (str.indexOf('"') >= 0 || str.indexOf("'") >= 0) {
    return false;
  }

  // Used by embedly:
  // /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/

  return /((http|https):\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?/i.test(
    _.trim(str)
  );
}

/**
 * Preplaces a character at a specific index with another character.
 * @param  {String} str       The source string.
 * @param  {Number} index     The character index which needs to be replaced.
 * @param  {String} character The character to place at the given index.
 * @return {String}           The new string.
 */
export function replaceAt(
  str: string,
  index: number,
  character: string
): string {
  if (!str || index < 0 || index >= str.length) {
    return str;
  }

  return (
    str.substr(0, index) + character + str.substr(index + character.length)
  );
}

/**
 * Replaces all instances of a string with another string.
 * @param  {String} str        The source string.
 * @param  {String} token      The string to replace.
 * @param  {String} newToken   The new string to replace the token with.
 * @return {String}            The replaced version of the string.
 */
export function replaceAll(
  str: string,
  token: string,
  newToken: string
): string {
  return str.split(token).join(newToken);
}

const NBSP_RAW = String.fromCharCode(160);

/**
 * Replaces non-breaking space characters with &nbsp;
 * @param  {String} str
 * @return {String}
 */
export function encodeNbsp(str: string): string {
  // @ts-expect-error - TS2554 - Expected 3 arguments, but got 4.
  return replaceAll(str, NBSP_RAW, '&nbsp;', false);
}

/**
 * Wrapper around tr.slugify to allow underscores.
 * @param  {String}   str             The original string.
 * @return {String}                   The slugified string.
 */
export function slugifyAllowUnderscores(str: string): string {
  return str.split(/_+/).map(slugify).join('_');
}

// truncate filenames collapsing int he center "longfile...name.jpg"
export function truncateMid(
  str: string,
  len: number,
  sep: string = '…'
): string {
  if (!str) {
    return '';
  }
  if (str.length <= len) {
    return str;
  }

  const sideLength = (len - sep.length) / 2;

  return (
    str.substr(0, Math.floor(sideLength)) +
    sep +
    str.substr(str.length - Math.ceil(sideLength))
  );
}

export function truncateEnd(
  str: string,
  len: number,
  sep: string = '…'
): string {
  return str.length <= len ? str : str.slice(0, len - sep.length) + sep;
}

/**
 * Renders a file size in a human readable format.
 * @param  {Number} fileSizeBytes The file size as reported by the uploader, e.g. 40960
 * @return {String}               The pretty file size, e.g. 40 kB
 */
export function prettySize(fileSizeBytes: number): string {
  if (!fileSizeBytes || fileSizeBytes < 0) {
    return 'Unknown';
  }

  const scale = Math.floor(Math.log(fileSizeBytes) / Math.log(1024));
  const precision = scale <= 1 ? 1 : 2;
  const number = Number(
    (fileSizeBytes / Math.pow(1024, scale)).toFixed(precision)
  );
  const units = ['B', 'kB', 'MB', 'GB', 'TB'][scale];

  return number + ' ' + units;
}

/**
 * Determines whether two strings have equal content (ignoring case and surrounding whitespace).
 * @param  {String}   str1  The first string.
 * @param  {String}   str2  The second string.
 * @return {Boolean}        True if the strings are equal.
 */
export function weakEqual(str1: string, str2: string): boolean {
  if (str1 === str2) {
    return true;
  }

  if ((isEmpty(str1) && hasValue(str2)) || (hasValue(str1) && isEmpty(str2))) {
    return false;
  }

  if (
    str1 !== null &&
    str2 !== null &&
    _.trim(str1.toUpperCase()) === _.trim(str2.toUpperCase())
  ) {
    return true;
  }

  return false;
}

/**
 * Determines if this is an empty (or undefined or null) string.
 * @param  {String}  str
 * @return {Boolean}
 */
export function isEmpty(str: string): boolean {
  return (
    (str === null && str === undefined) ||
    !_.isString(str) ||
    _.trim(str).length === 0
  );
}

/**
 * Converts a string to a hash.
 * @param  {String} str The input string.
 * @return {Number}     The output hash.
 */
export function hashCode(str: string): number {
  let hash = 0;
  let i;
  let j;
  let character;
  if (!str || str.length === 0) {
    return hash;
  }

  for (i = 0, j = str.length; i < j; i++) {
    character = str.charCodeAt(i);
    hash = (hash << 5) - hash + character;
    hash |= 0; // Convert to 32bit integer
  }

  return hash;
}

const testSuffix = test(/^\d+$/);
const someMax = Some(max);
const someOne = Some(1);

type namePair = [string, Option<number>];

type splitSuffixType = (arg1: string) => (arg1: string) => namePair;
export const splitSuffix: splitSuffixType = (separator) => (name: string) => {
  const parts = name.split(separator);
  const suffix = parts[parts.length - 1];
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  return parts.length > 1 && testSuffix(suffix)
    ? // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      [parts.slice(0, -1).join(separator), safeParseInt(suffix)]
    : [name, None];
};

type joinSuffixType = (arg1: string) => (arg1: namePair) => string;
export const joinSuffix: joinSuffixType =
  (separator) =>
  ([name, suffixOption]: [any, any]) =>
    maybe(name)((suffix) => name + separator + suffix)(suffixOption);

export const createConflictResolverWithSeparator = (separator: string) => {
  const split = splitSuffix(separator);
  const join = joinSuffix(separator);

  return (normalizer: (s: string) => string) =>
    (otherNames: Map<string, string>) =>
    (name: string): string => {
      const [nameWithoutSuffix, suffix] = split(name);
      const normalizedName = normalizer(nameWithoutSuffix);
      const suffixEquals = suffix.map(equals);

      const [conflict, highest] = otherNames.reduce(
        (result, otherName) => {
          const [hasConflict, highestSuffix] = result;
          const [otherNameWithoutSuffix, otherSuffix] = split(otherName);
          return normalizer(otherNameWithoutSuffix) === normalizedName
            ? [
                hasConflict ||
                  suffix === otherSuffix ||
                  extractBool(suffixEquals.ap(otherSuffix)),
                someMax.ap(highestSuffix).ap(otherSuffix.alt(someOne)),
              ]
            : result;
        },
        [false, someOne]
      );

      return conflict ? join([nameWithoutSuffix, highest.map(inc)]) : name;
    };
};

export const createConflictResolver = createConflictResolverWithSeparator(' ');

export const getPrefix: (arg3?: any) => string = compose(
  ([prefix]: [any]) => prefix
  // @ts-expect-error - TS2345 - Argument of type '(arg1: string) => namePair' is not assignable to parameter of type '(arg2: string) => [any]'.
)(splitSuffix(' '));

/**
 * Increment and append suffix to a string, based on a list of other strings
 * @param  {String[]}  collection    Array of strings to work against
 * @param  {String}    prefix        Prefix to use for the new string
 * @param  {String}    separator     Word separator to insert before suffix
 * @param  {Function}  stringAdapter String transform function
 * @return {String}                  Safely incremented string
 */
export function incrementString(
  collection: Array<string> | List<string>,
  prefix: string,
  separator: string | null | undefined,
  stringAdapter: (arg1: string) => string
): string {
  collection = collection || [];
  separator = separator || ' ';

  if (!prefix) {
    return '';
  }

  // If collection is empty, return singular
  // @ts-expect-error - TS2339 - Property 'length' does not exist on type 'string[] | List<string>'. | TS2339 - Property 'size' does not exist on type 'string[] | List<string>'.
  if (collection.length === 0 || collection.size === 0) {
    return prefix;
  }

  // Filter collection items to match adapted string prefix (with optional digits)
  const re = new RegExp('^' + stringAdapter(prefix) + '-?(\\d*)$', 'i');
  const slugs: Array<any | string> = [];
  collection.forEach(function (value) {
    value = stringAdapter(value);
    if (re.test(value)) {
      // Ignore implausibly large suffixes. Archiving a page renames its slug to
      // `<slug>-<timestamp>`, and that timestamp matches the digit group above, so
      // without this guard it would be parsed as the sequence number and produce
      // names like `My Page 1781809848311`. Filtering here (rather than in the
      // suffix loop below) also keeps the timestamp slug from blocking the
      // `!slugs.length` early return, so a freed base name stays unsuffixed.
      // `incrementWord` guards the same way.
      const m = value.match(re);
      const num = m && m[1] ? parseInt(m[1], 10) : 0;
      if (num <= MAX_INCREMENT) {
        slugs.push(value);
      }
    }
  });

  // If no matching slugs, return singular
  if (!slugs.length) {
    return prefix;
  }

  // Get the next highest suffix from slugs
  let suffix = 2;
  slugs.forEach(function (slug) {
    const matches = slug.match(re);

    // If slug does not have a digit suffix, we can safely default to 2
    if (!matches || matches[1] == null) {
      return;
    }

    // Otherwise, set the suffix to the highest number + one.
    const number = parseInt(matches[1], 10);
    if (number >= suffix) {
      suffix = number + 1;
    }
  });

  // Return new string w/ suffix
  return prefix + separator + suffix;
}

export function increment(
  collection: Array<string> | List<string>,
  prefix: string,
  separator?: string
): string {
  return incrementString(collection, prefix, separator, slugify);
}

// This is a version of the above "increment" function that WILL NOT call
// slugify on each string value in the collection.
// This exists because `tr.slugify` is a bottle neck in the hot path of creating
// a unique slug for new collection items in large large collections.
// When creating an increment string for a collection of "slug" strings we don't
// need to slugify the already slugified strings.
export function incrementSlug(
  collection: Array<string> | List<string>,
  prefix: string,
  separator?: string
): string {
  // No need to adapt the strings as they are already slugified.
  return incrementString(collection, prefix, separator, identity);
}

export function incrementWord(word: string, separator: string = '_'): string {
  const regExpMatch = separator + '(\\d+)$';
  const matched = word.match(new RegExp(regExpMatch));
  if (matched) {
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    let currentIndex = parseInt(matched[1], 10);

    // Note: we get an ye olde infinite loop here if matched[1] was above
    // 15484832551465742000 because (15484832551465742000 === 15484832551465742000+1)
    // So if we have a large number here just create a new incrementor
    if (currentIndex > MAX_INCREMENT) {
      return word + separator + '1';
    } else {
      const regExpReplace = `(.*${separator})(\\\d+)`;
      return word.replace(new RegExp(regExpReplace), function (_match, body) {
        currentIndex++;
        return body + currentIndex;
      });
    }
  } else {
    return word + separator + '1';
  }
}

/**
 * Increments a filename as many operating systems do when clashes occur. E.g. filename.jpg becomes filename_1.jpg.
 * @param  {String}  filename    Filename string to increment. Can also be a filepath (with slashes).
 * @return {String}              Incremented filename.
 **/

function incrementFilename(fileName: string): string {
  const pieces = fileName.split('.');
  if (pieces.length > 1) {
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    pieces[pieces.length - 2] = incrementWord(pieces[pieces.length - 2]);
    return pieces.join('.');
  } else {
    const fileNameBody = pieces[pieces.length - 1];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    return incrementWord(fileNameBody);
  }
}

/**
 * Given a set of filenames or paths and a filename, it will keep incrementing the filename until it no longer clashes with the set.
 * Note: to work with case-sensitive file systems, `collection` is expected to have been converted to lowercase.
 * @param    {Array}   collection  A set of lowercased filenames with which the resulting filename is supposed to not clash with when converted to lowercase
 * @param    {String}  fileName    The mixed-case filename to be incremented until it no longer clashes with the set.
 * @return   {String}              Incremented filename.
 **/

export function nextAvailableFileName(
  collection: Array<string>,
  fileName: string
): string {
  if (_.includes(collection, fileName.toLowerCase())) {
    return nextAvailableFileName(collection, incrementFilename(fileName));
  } else {
    return fileName;
  }
}

/**
 * There are number of cases where special characters and uri encoded escape sequences can cause problems. Including filenames that have been through
 * multiple encodeURIComponent passes, and unicode filenames that include multi-byte unicode code points.
 * @param  {String} fileName fileName that may or may not have been through multiple encodeURIComponent passes
 * @return {String}          cleaned, human readible filename
 */
export function cleanFileNameForExport(fileName: string): string {
  if (!fileName || !_.isString(fileName)) {
    return '';
  }

  // Run filename through encodeURIComponent to prevent multi-byte unicode code points from breaking decodeURIComponent after they're escaped()
  let escapedFileName = encodeURIComponent(fileName);
  let cleanedFileName = '';

  // Unescape until there's nothing left to unescape so we don't introduce alphanumeric parts of escape sequences into the filename
  while (escapedFileName !== cleanedFileName) {
    cleanedFileName = escapedFileName;
    escapedFileName = unescape(escapedFileName);
  }

  // remove free-standing % symbols that would throw a decodeURIComponent error
  cleanedFileName = cleanedFileName.replace(/%/g, '');

  // unescape() will produce single-byte unicode code points, which is not great for most languages, but helped us clean things up.
  // decodeURIComponent will change these into the correct unicode characters after an escape
  escapedFileName = escape(cleanedFileName);
  try {
    cleanedFileName = decodeURIComponent(escapedFileName);
  } catch (error: any) {
    cleanedFileName = escapedFileName.replace(/%/g, '');
  }

  return cleanedFileName
    .replace(/\s/g, '-') // replace spaces with hyphens
    .replace(/[!*'();:@&=+$,/?#\\[\]%]/g, ''); // remove certain special characters
}

/**
 * Strips out any character that is not a valid number.
 * @param  {String} string Input string to rebuild without non numeric characters
 * @return {String}        Output string with only valid numeric characters
 */
export function stripNonNumeric(string: string): string {
  return string.split('').reduce((result, char) => {
    return (char >= '0' && char <= '9') ||
      (char === '-' && !result.length) ||
      (char === '.' && result.indexOf('.') < 0)
      ? result + char
      : result;
  }, '');
}

/**
 * Counts the number of times a substring occurs inside of a string.
 * Based on http://stackoverflow.com/a/7924240 - ~10x faster than a String.prototype.match()
 * @param  {String} string    The string to search through.
 * @param  {String} subString The substring to search for.
 * @return {Number}           The number of times the subString appears in the string.
 */
export function countOccurences(string: string, subString: string): number {
  if (subString.length <= 0) {
    return string.length + 1;
  }

  let count = 0;
  let pos = 0;
  const step = subString.length;

  /* eslint-disable no-constant-condition */
  while (true) {
    pos = string.indexOf(subString, pos);
    if (pos >= 0) {
      ++count;
      pos += step;
    } else {
      break;
    }
  }
  /* eslint-enable no-constant-condition */

  return count;
}

/**
 * A safe encodedURIComponent call.
 * @param {String} string The string to encode with encodeUriCompnent
 * @return {Result} Ok when the encoding succeedes, an Err on failure.
 */
export function encodeUriComponent(string: string): Result<string, string> {
  try {
    return Ok(encodeURIComponent(string));
  } catch (e: any) {
    return Err(e.message);
  }
}

export function pluralize(
  qty: number,
  singular: string,
  plural: string = `${singular}s`
): string {
  if (qty === 1) {
    return singular;
  }

  return plural;
}

/**
 * Removes extra indentation from a string and trims empty lines at the
 * beginning/end.
 */
export const normalizeIndentation = (str: string): string => {
  const lines = str.split('\n');
  let minIndentation = Infinity;
  let firstRealLine;
  let lastRealLine;

  for (let idx = 0; idx < lines.length; idx += 1) {
    const line = lines[idx];
    // @ts-expect-error - TS18048 - 'line' is possibly 'undefined'.
    if (line.trim().length === 0) continue;

    minIndentation = Math.min(
      // @ts-expect-error - TS18048 - 'line' is possibly 'undefined'. | TS18048 - 'line' is possibly 'undefined'.
      line.length - line.trimLeft().length,
      minIndentation
    );

    firstRealLine = firstRealLine ?? idx;
    lastRealLine = idx;
  }

  if (firstRealLine == null || lastRealLine == null)
    throw new Error('Failed to normalize string indentation');

  return lines
    .slice(firstRealLine, lastRealLine + 1)
    .map((line) => line.substr(minIndentation))
    .join('\n');
};

/**
 * Gets an accurate count of characters in a string, counting double-byte characters as one.
 * Use this instead of String.prototype.length to avoid cases like this:
 * 'थि'.length === 2
 * getCharacterCount('थि') === 1
 */
export const getCharacterCount = (str: string): number => {
  const space = '\\p{Zs}';
  const singleChar = new RegExp(
    `[${space}\\p{Letter}\\p{Number}\\p{Punctuation}\\p{Symbol}]`,
    'igu'
  );
  const chars = str.match(singleChar);
  return chars == null ? 0 : chars.length;
};
